import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { Outlet, useOutletContext, useParams, useNavigate } from 'react-router-dom'
import { Stack, Box } from '@mui/material'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { useUser, countUserSites, useSites } from 'store'
import StreepMap from 'components/Cartography/Cartography'
import MapLoadingSkeleton from 'components/global/CUK/Skeletons/MapLoadingSkeleton'
import useFocus from 'components/AuthenticatedLayout/Header/AccountsPerimeter/use-focus'
import EmptyScreen from 'packages/ui/EmptyScreen'
import useSubscribeRefresh  from 'use-subscribe-actions'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'
import LoaderSitesMetrics from 'components/Cartography/LoaderMetrics'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'

import { SitesStatuses } from '../../Topbar/Filters/SitesFilter'
import { normalizeString } from '../helpers'
import { useFilters, useResetFilters } from '../../store'
import AccountsList from './AccountsList'
import useSite from './use-site'
import useBuildings from './use-buildings'
import { Navigate } from '../../../../router'
import IndoorMap from './IndoorMap'
import AccountPanel from './AccountPanel'
import Card from './Card'
import { TableContent, MainContent } from '../../../PagesStyles'
import TextFilter from '../../Topbar/Filters/TextFilter'
import ExportButton from '../../Topbar/ExportButton'
import useExport from '../Sites/use-export'

const OPERATION_FIND_SURFACE_AREAS = gql`
  query FindSurfaceAreas($site_id: ID!, $sf_id: String!) {
    surfaces: findSurfaceAreas(site_id: $site_id) {
      uuid
      name
      location {
        latitude
        longitude
      }
    }
    site: findSite(sf_id: $sf_id) {
      ... on Site {
        building_equipment {
          name
          available_level
        }
      }
    }
  }
`

const convertSiteWithSurfaces = (site, surfaces, buildingEquipments) => {
  return surfaces.map(surface => ({
    ...site,
    buildingId: surface.uuid,
    buildingEquipment: buildingEquipments.find(({ name }) => name === surface.name),
    position: {
      lng: surface.location.longitude,
      lat: surface.location.latitude
    },
    isSurfaceArea: true,
    name: surface.name
  }))
}

export const HomeRedirect = () => {
  const { buildingId, focusId} = useParams()
  const { loading, redirect } = useBuildings(focusId)
  return loading ? <MapLoadingSkeleton /> : <Navigate pathname={redirect(buildingId)} />
}

export const HomeDevicesMap = () => {
  const { isExpanded, handleToggleExpand, sites } = useOutletContext()
  return (
    <IndoorMap
      sites={sites}
      isExpanded={isExpanded}
      onToggleExpand={handleToggleExpand}
    />
  )
}

export const HomeNoSurfaceArea = () => {
  const {t} = useTranslation()
  return (
    <EmptyScreen icon="buildings" variant="map">{t('supervision.no_surface_area')}</EmptyScreen>
  )
}
export const HomeGoogleMap = () => {
  const { sites } = useOutletContext()
  const navigate = useNavigate()
  const { focusId } = useParams()
  const {onFocus} = useFocus()
  const focusSite = useMemo(() => {
    return sites.find(({sf_id}) => sf_id === focusId)
  }, [focusId, sites])

  const { data } = useQuery(OPERATION_FIND_SURFACE_AREAS, {
    variables: {
      site_id: focusSite?.id,
      sf_id: focusSite?.sf_id,
    },
    skip: !focusSite
  })

  const sitesOnMap = useMemo(() => {
    if (focusSite) {
      if (data && data.surfaces.length > 1) {
        return convertSiteWithSurfaces(
          focusSite,
          data.surfaces,
          data.site?.building_equipment ?? []
        )
      }
      return sites.filter(({sf_id}) => sf_id === focusSite.sf_id)
    }
    return sites
  }, [sites, focusSite, data])

  const keyMap = useMemo(() => {
    return sitesOnMap.map(({ sf_id }) => sf_id).join()
  }, [sitesOnMap])

  return (
    <StreepMap
      key={keyMap}
      sites={sitesOnMap}
      onMarkerClick={(marker) => {
        onFocus(marker)
        navigate(`/monitoring/map/${marker.sf_id}/redirect_first_floor/${marker.buildingId || ''}`)
      }}
      isSupervisionView={true}
      showExpandButton={false}
    />
  )
}

export const HomeForceFocus = ({children}) => {
  const {focusId} = useParams()
  const [user] = useUser()
  const [{selected}] = useSites()

  const hasOneSite = useMemo(() => countUserSites(user) === 1, [user])
  const mustRedirect = useMemo(() => {
    if (!selected.length || !hasOneSite) return false
    return !focusId || focusId !== selected[0].sf_id
  }, [selected, hasOneSite, focusId])

  return mustRedirect
    ? <Navigate pathname={`/monitoring/map/${selected[0].sf_id}/redirect_first_floor`} />
    : children
}

const Sidebar = () => {
  const { deviceId, focusId, buildingId, floorId } = useParams()
  const { loading, site, mapEquipments: equipments } = useSite(focusId)
  const navigate = useNavigate()
  const device = useMemo(() => equipments.find(({ uuid }) => uuid === deviceId), [deviceId, equipments])

  if (loading) {
    return (
      <Card
        loading={true}
        onCloseClick={() =>
          navigate({
            pathname: `/monitoring/map/${focusId}/${buildingId}/${floorId}`,
          })
        }
      />
    )
  } else if (!device) {
    return <Navigate pathname={`/monitoring/map/${focusId}/${buildingId}/${floorId}`} />
  }
  return (
    <Card
      site={site}
      device={device}
      building={device.building}
      floor={device.floor}
      onCloseClick={() =>
        navigate({
          pathname: `/monitoring/map/${focusId}/${buildingId}/${floorId}`,
        })
      }
    />
  )
}

export const HomeMain = ({sites}) => {
  const { buildingId, floorId, deviceId } = useParams()
  const [filters, setFilters] = useFilters()
  const resetFilters = useResetFilters()
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()

  useSubscribeRefresh(() => {
    resetFilters()
    navigate('/monitoring/map')
  })

  const filtersStatusFct = useCallback(
    (site) => filters.status.includes(site.equipment.available_level),
    [filters.status],
  )
  const filterVerbatimFct = useCallback(
    (site) => normalizeString(site.name).includes(normalizeString(filters.verbatim)),
    [filters.verbatim],
  )
  const filteredSites = useMemo(() => {
    const filtersFct = [
      filters.status.length ? filtersStatusFct : null,
      filters.verbatim.length ? filterVerbatimFct : null,
    ].filter(Boolean)
    if (filtersFct.length) {
      return sites.filter((site) => filtersFct.every((fct) => fct(site)))
    }
    return sites
  }, [filters, sites])
  const createExport = useExport({sites: filteredSites})
  useRegisterBreadcrumbAction(<ExportButton analyticsName="monitoring_map" onClick={async () => { await createExport() }} />)

  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev)
  }

  useEffect(() => {
    resetFilters()
  }, [])
  return (
    <>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Stack direction={'row'} gap={1}>
          <SitesStatuses
            sites={filteredSites}
            onChange={(values) => {
              setFilters({ ...filters, status: values })
            }}
            values={filters.status}
          />
          {
            !buildingId && <TextFilter />
          }
        </Stack>
      </Stack>
      <MainContent mode={'expand'}>
        <Stack
          direction="row"
          sx={{
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            {
              floorId
                ? !isExpanded && (
                <AccountPanel sites={sites} />
              )
                : (
                  <AccountsList
                    sites={filteredSites}
                    onClick={({sf_id}) => {
                      navigate(`/monitoring/map/${sf_id}`)
                    }}
                  />
                )
            }
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Outlet context={{sites: filteredSites, isExpanded, handleToggleExpand}}></Outlet>
          </Box>
          {
            deviceId && (
              <Box sx={{
                width: '400px',
                display: 'flex',
                flexShrink: 0,
                height: '100%'
              }}>
                <Sidebar />
              </Box>
            )
          }
        </Stack>
      </MainContent>
    </>
  )
}

const Loader = () => {
  const resetFilters = useResetFilters()
  useEffect(() => {
    return () => resetFilters()
  }, [])
  return (
    <Stack gap={1} sx={{ flex: 1, minHeight: 0 }}>
      <LoaderSitesMetrics loader={<TableContent mode={'expand'}><TableSkeleton /></TableContent>}>
        {({ sites }) => (
          <HomeMain sites={sites} />
        )}
      </LoaderSitesMetrics>
    </Stack>
  )
}

export default Loader
